// src/components/notifications/NotificationDropdown.jsx - FIXED VERSION
import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck, Trash2, RefreshCw, Truck, FileText, AlertCircle, MessageSquare, Square, CheckSquare } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';

const NotificationDropdown = ({ onClose }) => {
  const { t, language } = useLanguage();
  const dropdownRef = useRef(null);
  const {
    notifications,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    bulkMarkAsRead,
    bulkDelete,
    deleteNotification,
    refreshNotifications
  } = useNotifications();
  
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Handle click outside to close - FIXED
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the dropdown AND not on the bell button
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          !event.target.closest('[data-notification-trigger]')) {
        onClose();
      }
    };

    // Add event listener immediately
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Fetch notifications when component mounts
  useEffect(() => {
    const loadNotifications = async () => {
      const data = await fetchNotifications(1, 20);
      setHasMore(data.has_next);
    };
    loadNotifications();
  }, []);

  // Update isAllSelected when notifications or selected change
  useEffect(() => {
    if (notifications.length > 0) {
      setIsAllSelected(notifications.every(n => selectedNotifications.has(n.id)));
    }
  }, [notifications, selectedNotifications]);

  // Translate notification content ONLY for system-generated notifications
  const translateNotificationContent = (notification) => {
    const { title, message, notification_type } = notification;
    
    // Manual notifications are NEVER translated - display exactly as admin wrote them
    if (notification_type === 'manual') {
      return { title, message };
    }

    // Define system-generated notification types that should be translated
    const systemTypes = [
      'registration_created',
      'registration_updated', 
      'registration_deleted',
      'invoice_created',
      'invoice_updated',
      'invoice_deleted',
      'invoice_paid',
      'transaction_added',
	  'insurance_signed',
	  'insurance_expires'
	  
    ];

    // If it's NOT a system type, return original content
    if (!notification_type || !systemTypes.includes(notification_type)) {
      return { title, message };
    }

    // Only translate system-generated notifications
    // Extract vehicle number or invoice number from the message
    const vehicleMatch = message.match(/vehicle\s+([A-Z0-9-]+)/i);
    const invoiceMatch = message.match(/invoice\s+([A-Z0-9-]+)/i);
    const companyMatch = message.match(/for\s+([^.]+)/);
	const insuranceVehicle = message.match(/insurance for\s+([A-Z0-9-]+)/i);
    const insuranceDaysMatch = message.match(/expires in (\d+) days/i);
	const expirestoday = message.includes('today');
	const expirestomorrow = message.includes('tomorrow');
	
    const vehicleNumber = vehicleMatch ? vehicleMatch[1] : '';
    const invoiceNumber = invoiceMatch ? invoiceMatch[1] : '';
    const company = companyMatch ? companyMatch[1].replace(/\s+(has been|details).*/, '') : '';
	const insuranceVehNumber = insuranceVehicle ? insuranceVehicle[1] : '';
	const insuranceExpireDays = insuranceDaysMatch ? insuranceDaysMatch[1] : '';
	const expireDaysText = expirestoday ? t('today') : expirestomorrow ? t('tomorrow') : `${language === 'ru' ? t('through'): language === 'en' ? 'in' : ''} ${insuranceExpireDays + ' ' + t('notifDays')}`;

	
    // Translate based on notification type
    switch (notification_type) {
      case 'registration_created':
        return {
          title: t('registrationCreated') || 'Registration Created',
          message: (t('registrationCreatedMsg') || 'Registration for vehicle {number} has been created').replace('{number}', vehicleNumber)
        };
      case 'registration_updated':
        return {
          title: t('registrationUpdated') || 'Registration Updated',
          message: (t('registrationUpdatedMsg') || 'Registration for vehicle {number} has been updated').replace('{number}', vehicleNumber)
        };
      case 'registration_deleted':
        return {
          title: t('registrationDeleted') || 'Registration Deleted',
          message: (t('registrationDeletedMsg') || 'Registration for vehicle {number} has been deleted').replace('{number}', vehicleNumber)
        };
      case 'invoice_created':
        return {
          title: t('invoiceCreated') || 'Invoice Created',
          message: (t('invoiceCreatedMsg') || 'Invoice {invoiceNumber} for {company} has been created').replace('{invoiceNumber}', invoiceNumber).replace('{company}', company)
        };
      case 'invoice_updated':
        return {
          title: t('invoiceUpdated') || 'Invoice Updated',
          message: (t('invoiceUpdatedMsg') || 'Invoice {invoiceNumber} has been updated').replace('{invoiceNumber}', invoiceNumber)
        };
      case 'invoice_deleted':
        return {
          title: t('invoiceDeleted') || 'Invoice Deleted',
          message: (t('invoiceDeletedMsg') || 'Invoice {invoiceNumber} has been deleted').replace('{invoiceNumber}', invoiceNumber)
        };
      case 'invoice_paid':
        return {
          title: t('invoicePaid') || 'Invoice Paid',
          message: `Invoice ${invoiceNumber} has been paid`
        };
      case 'transaction_added':
        return {
          title: t('transactionAdded') || 'Transaction Added',
          message: `New transaction added for ${company}`
        };
	  case 'insurance_signed':
        return {
          title: t('insurance_signed') || 'Insurance Signed',
          message: (t('insurance_signedMsg') || 'Pre-ordered insurance for {plate_number} has been signed.').replace('{plate_number}', insuranceVehNumber)
        };
	  case 'insurance_expires':
        return {
          title: t('insurance_expires') || 'Insurance Expires',
          message: (t('insurance_expiresMsg') || 'Insurance for {plate_number} ({country}) expires in {expireDays}.')
         .replace(/\{(\w+)\}/g, (match, key) => {
    const replacements = {
      'plate_number': insuranceVehNumber,
      'expireDays': expireDaysText
    };
    return replacements[key] || match;
  })
        };
      default:
        return { title, message };
    }
  };

  const handleLoadMore = async (event) => {
    event.stopPropagation();
    const nextPage = page + 1;
    const data = await fetchNotifications(nextPage, 20);
    setPage(nextPage);
    setHasMore(data.has_next);
  };

  const handleMarkAsRead = async (notificationId, event) => {
    event.preventDefault();
    event.stopPropagation();
    await markAsRead(notificationId);
  };

  const handleMarkAllAsRead = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await markAllAsRead();
  };

  const handleDelete = async (notificationId, event) => {
    event.preventDefault();
    event.stopPropagation();
    await deleteNotification(notificationId);
    
    // Remove from selected if it was selected
    const newSelected = new Set(selectedNotifications);
    newSelected.delete(notificationId);
    setSelectedNotifications(newSelected);
  };

  const handleRefresh = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedNotifications(new Set());
    refreshNotifications();
  };

  // Bulk selection handlers
  const handleSelectAll = (event) => {
    event.stopPropagation();
    if (isAllSelected) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(notifications.map(n => n.id)));
    }
  };

  const handleSelectNotification = (notificationId, event) => {
    event.stopPropagation();
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(notificationId)) {
      newSelected.delete(notificationId);
    } else {
      newSelected.add(notificationId);
    }
    setSelectedNotifications(newSelected);
  };

  // Bulk actions
  const handleBulkMarkAsRead = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (selectedNotifications.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      await bulkMarkAsRead(Array.from(selectedNotifications));
      setSelectedNotifications(new Set());
    } catch (error) {
      console.error('Bulk mark as read failed:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (selectedNotifications.size === 0) return;
    if (!window.confirm(t('confirmBulkDelete') || `Delete ${selectedNotifications.size} selected notifications?`)) return;
    
    setBulkActionLoading(true);
    try {
      await bulkDelete(Array.from(selectedNotifications));
      setSelectedNotifications(new Set());
    } catch (error) {
      console.error('Bulk delete failed:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'registration_created':
      case 'registration_updated':
      case 'registration_deleted':
        return <Truck className="h-4 w-4 text-blue-500" />;
      case 'invoice_created':
      case 'invoice_updated':
      case 'invoice_deleted':
      case 'invoice_paid':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'transaction_added':
        return <FileText className="h-4 w-4 text-purple-500" />;
      case 'manual':
        return <MessageSquare className="h-4 w-4 text-orange-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return t('justNow') || 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ${t('ago') || 'ago'}`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ${t('ago') || 'ago'}`;
    }
  };

  const selectedCount = selectedNotifications.size;
  const hasUnreadSelected = notifications.some(n => selectedNotifications.has(n.id) && !n.is_read);

  return (
    <div 
      ref={dropdownRef}
      className="w-full sm:w-96 bg-white rounded-lg shadow-2xl border border-gray-200 max-h-[85vh] sm:max-h-[600px] overflow-hidden mx-3 sm:mx-0"
      style={{ zIndex: 10002 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Bell className="h-5 w-5 mr-2 text-blue-600" />
          {t('notifications') || 'Notifications'}
          {selectedCount > 0 && (
            <span className="ml-2 bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full">
              {selectedCount}
            </span>
          )}
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRefresh}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
            title={t('refresh') || 'Refresh'}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedCount > 0 && (
        <div className="px-4 py-3 border-b border-gray-200 bg-blue-50 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-blue-800">
              {selectedCount} {t('selected') || 'selected'}
            </span>
            {hasUnreadSelected && (
              <button
                onClick={handleBulkMarkAsRead}
                disabled={bulkActionLoading}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center font-medium transition-colors disabled:opacity-50"
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                {t('markAsRead') || 'Mark as read'}
              </button>
            )}
            <button
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="text-sm text-red-600 hover:text-red-800 flex items-center font-medium transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {t('delete') || 'Delete'}
            </button>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedNotifications(new Set());
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {t('clearSelection') || 'Clear'}
          </button>
        </div>
      )}

      {/* Quick Actions */}
      {notifications.some(n => !n.is_read) && selectedCount === 0 && (
        <div className="px-4 py-3 border-b border-gray-200 bg-blue-50 flex items-center justify-between">
          <button
            onClick={handleMarkAllAsRead}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center font-medium transition-colors"
          >
            <CheckCheck className="h-4 w-4 mr-1" />
            {t('markAllAsRead') || 'Mark all as read'}
          </button>
          
          {notifications.length > 0 && (
            <button
              onClick={handleSelectAll}
              className="text-sm text-gray-600 hover:text-gray-800 flex items-center transition-colors"
            >
              <CheckSquare className="h-4 w-4 mr-1" />
              {t('selectAll') || 'Select all'}
            </button>
          )}
        </div>
      )}

      {/* Notifications List */}
      <div className="overflow-y-auto max-h-[calc(85vh-280px)] sm:max-h-96">
        {isLoading && notifications.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400 mr-2" />
            <span className="text-gray-500">{t('loading') || 'Loading...'}</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-gray-500">
            <Bell className="h-16 w-16 text-gray-300 mb-3" />
            <p className="text-sm font-medium">{t('noNotifications') || 'No notifications'}</p>
            <p className="text-xs text-gray-400 mt-1">{t('notificationsWillAppearHere') || 'Notifications will appear here'}</p>
          </div>
        ) : (
          <>
            {notifications.map((notification) => {
              const translatedContent = translateNotificationContent(notification);
              const isSelected = selectedNotifications.has(notification.id);
              
              return (
                <div
                  key={notification.id}
                  className={`px-4 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    !notification.is_read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  } ${isSelected ? 'bg-blue-100' : ''}`}
                >
                  <div className="flex items-start space-x-3">
                    {/* Selection Checkbox */}
                    <button
                      onClick={(e) => handleSelectNotification(notification.id, e)}
                      className="flex-shrink-0 mt-1 p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      {isSelected ? (
                        <CheckSquare className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Square className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                    
                    {/* Notification Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 pr-2">
                          <p className={`text-sm leading-5 ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {translatedContent.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 leading-4 break-words">
                            {translatedContent.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            {formatDate(notification.created_at)}
                          </p>
                        </div>
                        
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          {!notification.is_read && (
                            <button
                              onClick={(e) => handleMarkAsRead(notification.id, e)}
                              className="p-1.5 text-blue-500 hover:text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                              title={t('markAsRead') || 'Mark as read'}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            onClick={(e) => handleDelete(notification.id, e)}
                            className="p-1.5 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100 transition-colors"
                            title={t('delete') || 'Delete'}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Load More Button */}
            {hasMore && (
              <div className="p-4 text-center border-t border-gray-200 bg-gray-50">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 font-medium transition-colors"
                >
                  {isLoading ? (t('loading') || 'Loading...') : (t('loadMore') || 'Load more')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationDropdown;